import { create } from 'zustand'
import useAuth from './useAuth'
import api from '../utils/api'

// Module-level cache
let _flags = null
let _fetchedAt = 0
const TTL = 60_000

const useFeatureFlags = create((set) => ({
  flags: [],
  loaded: false,
  fetch: async () => {
    if (_flags && Date.now() - _fetchedAt < TTL) {
      set({ flags: _flags, loaded: true })
      return
    }
    try {
      const { data } = await api.get('/features')
      _flags = data
      _fetchedAt = Date.now()
      set({ flags: data, loaded: true })
    } catch {
      // If fetch fails, keep old flags or empty
      set({ loaded: true })
    }
  },
  invalidate: () => {
    _flags = null
    _fetchedAt = 0
  },
}))

/**
 * Hook that returns feature access booleans based on user's role + plan + feature flags.
 *
 * Usage:
 *   const { can, role } = useFeatureAccess()
 *   if (can('signals')) ...
 *   if (can('call_channel')) ...
 */
export default function useFeatureAccess() {
  const profile = useAuth((s) => s.profile)
  const token = useAuth((s) => s.token)
  const { flags, loaded, fetch } = useFeatureFlags()

  // Fetch flags on first call if we have a token
  if (token && !loaded) fetch()

  const role = profile?.role || (profile?.is_admin ? 'admin' : 'user')
  const plan = profile?.subscription_plan || 'free'

  function can(key) {
    // Admin always has access to everything
    if (role === 'admin') return true

    // These features are NEVER permissive — must be explicitly granted
    if (key === 'admin_panel' || key === 'signals') return false

    // Testers get full access to all non-admin features
    if (role === 'tester') return true

    // If flags haven't loaded or loaded empty (API error), be permissive for non-admin features
    if (!flags || flags.length === 0) return true

    const flag = flags.find(f => f.key === key)
    // Unknown flag key = not gated = allow
    if (!flag) return true
    if (!flag.enabled) return false
    return (flag.allowed_plans || []).includes(plan) || (flag.allowed_roles || []).includes(role)
  }

  return { can, role, plan, loaded }
}

// Re-export the store for invalidation from admin
export { useFeatureFlags }
